import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard, Users as UsersIcon, PhoneCall, Activity, Cog, UserPlus,
  Wallet, ShieldCheck, Trash2, Pencil, PlusCircle, X, CheckCircle2, XCircle, ArrowDownRight, ArrowUpRight,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { PriceChart } from "@/components/CryptoChart";
import { useAuth } from "@/context/AuthContext";
import api, { fmtCurrency, fmtDate, formatApiErrorDetail } from "@/lib/api";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "leads", label: "Leads", icon: PhoneCall },
  { id: "transactions", label: "Transactions", icon: Activity },
  { id: "settings", label: "Settings", icon: Cog },
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="admin-modal">
      <div className="absolute inset-0 bg-sapphire/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative glass-strong rounded-2xl w-full max-w-lg p-6 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function AdminConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [txns, setTxns] = useState([]);
  const [modal, setModal] = useState(null); // {type, user}
  const [busy, setBusy] = useState(false);
  const [coins, setCoins] = useState([]);

  useEffect(() => {
    api.get("/crypto/prices").then(({ data }) => setCoins(data.coins)).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [s, u, l, t] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/leads"), api.get("/admin/transactions"),
      ]);
      setStats(s.data); setUsers(u.data.users); setLeads(l.data.leads); setTxns(t.data.transactions);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const closeModal = () => setModal(null);

  const doCreate = async (form) => {
    setBusy(true);
    try {
      await api.post("/admin/users", { ...form, balance: parseFloat(form.balance || 0) });
      toast.success("User created");
      closeModal(); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doEdit = async (id, form) => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (payload.withdraw_tax_percent === "" || payload.withdraw_tax_percent == null) {
        delete payload.withdraw_tax_percent;
      } else {
        payload.withdraw_tax_percent = parseFloat(payload.withdraw_tax_percent);
      }
      if (payload.exchange_rate === "" || payload.exchange_rate == null) {
        delete payload.exchange_rate;
      } else {
        payload.exchange_rate = parseFloat(payload.exchange_rate);
      }
      await api.put(`/admin/users/${id}`, payload);
      toast.success("User updated");
      closeModal(); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doBalance = async (id, amount, description) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${id}/balance`, { amount: parseFloat(amount), description });
      toast.success("Balance adjusted");
      closeModal(); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleVerify = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/verify`);
      toast.success(`${u.email} ${u.verified ? "unverified" : "verified"}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their transactions too.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setLeadStatus = async (lead, status) => {
    try { await api.put(`/admin/leads/${lead.id}`, { status }); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead ${lead.email}?`)) return;
    try { await api.delete(`/admin/leads/${lead.id}`); toast.success("Lead deleted"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteTxn = async (t) => {
    if (!window.confirm("Delete this transaction record?")) return;
    try { await api.delete(`/admin/transactions/${t.id}`); toast.success("Transaction deleted"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="relative min-h-screen">
      <div className="clu-ambient" />
      <div className="relative z-10">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6" data-testid="admin-dashboard">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold">/ Console</p>
              <p className="text-sm text-white/60 mt-1 truncate">{user?.email}</p>
            </div>
            <nav className="p-2 flex lg:flex-col gap-1 overflow-x-auto">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setTab(n.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap ${tab === n.id ? "bg-gold text-sapphire font-semibold" : "text-white/70 hover:bg-white/5"}`}>
                  <n.icon className="w-4 h-4" /><span>{n.label}</span>
                  {n.id === "leads" && leads.length > 0 && (
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded font-mono ${tab === n.id ? "bg-sapphire/20" : "bg-white/10 text-white/70"}`}>{leads.length}</span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          <main className="min-w-0">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">/ Admin Control</p>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Operations Console</h1>
              </div>
              <button data-testid="open-create-user-btn" onClick={() => setModal({ type: "create" })}
                className="bg-gold hover:bg-gold-soft text-sapphire font-semibold px-4 py-2.5 rounded-md flex items-center gap-2 transition-colors">
                <UserPlus className="w-4 h-4" /> Add User
              </button>
            </div>

            {/* OVERVIEW */}
            {tab === "overview" && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Total Users", value: stats.total_users, icon: UsersIcon },
                    { label: "Admins", value: stats.total_admins, icon: ShieldCheck },
                    { label: "Transactions", value: stats.total_transactions, icon: Activity },
                    { label: "Total Balance", value: fmtCurrency(stats.total_balance), icon: Wallet, mono: true },
                  ].map((s) => (
                    <div key={s.label} className="glass rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-mono uppercase tracking-widest text-white/50">{s.label}</span>
                        <s.icon className="w-4 h-4 text-gold" />
                      </div>
                      <div className={`${s.mono ? "font-mono text-xl" : "font-display text-2xl"} font-bold tracking-tight`}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {(() => { const btc = coins.find((c) => c.symbol === "BTC"); return (
                <div className="glass rounded-2xl p-6" data-testid="admin-market-chart">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-gold" />
                      <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Bitcoin · 7-Day Market</span>
                    </div>
                    {btc && <span className="font-mono text-lg font-bold">{fmtCurrency(btc.price)}</span>}
                  </div>
                  <PriceChart data={btc?.sparkline || []} color="#D4AF37" height={200} />
                </div>
                ); })()}

                <div className="glass rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gold" />
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Latest Activity</p>
                  </div>
                  <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
                    {stats.recent_activity.length === 0 && <div className="px-6 py-10 text-center text-white/40 text-sm">No activity yet.</div>}
                    {stats.recent_activity.map((t) => (
                      <div key={t.id} className="px-6 py-3 flex justify-between items-center hover:bg-white/5 transition-colors">
                        <div className="text-xs">
                          <div className="text-white/70">{t.description}</div>
                          <div className="text-white/30 font-mono mt-1">{fmtDate(t.created_at)} · {t.user_email}</div>
                        </div>
                        <div className={`font-mono text-sm font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {t.amount >= 0 ? "+" : "-"}{fmtCurrency(Math.abs(t.amount))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* USERS */}
            {tab === "users" && (
              <div className="glass rounded-2xl overflow-hidden" data-testid="users-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-white/50 font-mono text-xs uppercase tracking-widest border-b border-white/10">
                        <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Password</th><th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Balance</th><th className="px-4 py-3">Tax %</th><th className="px-4 py-3">Verified</th><th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-white/5 transition-colors" data-testid="user-row">
                          <td className="px-4 py-3 whitespace-nowrap">{u.name || "—"}</td>
                          <td className="px-4 py-3 text-white/70">{u.email}</td>
                          <td className="px-4 py-3 font-mono text-gold" data-testid="user-password-cell">{u.plain_password ? u.plain_password : "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${u.role === "admin" ? "bg-gold/15 text-gold" : "bg-white/10 text-white/60"}`}>{u.role}</span>
                          </td>
                          <td className="px-4 py-3 font-mono">{fmtCurrency(u.balance, u.currency)}</td>
                          <td className="px-4 py-3 font-mono text-white/70" data-testid="user-tax-cell">{u.role === "admin" ? "—" : (u.verified ? "0%" : (u.withdraw_tax_enabled === false ? "Off" : `${u.withdraw_tax_percent ?? 10}%`))}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => toggleVerify(u)} data-testid="toggle-verify-btn"
                              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${u.verified ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-white/50"}`}>
                              {u.verified ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                              {u.verified ? "Verified" : "Unverified"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button title="Adjust balance" data-testid="adjust-balance-btn" onClick={() => setModal({ type: "balance", user: u })} className="p-1.5 rounded hover:bg-white/10 text-gold"><Wallet className="w-4 h-4" /></button>
                              <button title="Add transaction" data-testid="add-txn-btn" onClick={() => setModal({ type: "txn", user: u })} className="p-1.5 rounded hover:bg-white/10 text-emerald-400"><PlusCircle className="w-4 h-4" /></button>
                              <button title="Edit" data-testid="edit-user-btn" onClick={() => setModal({ type: "edit", user: u })} className="p-1.5 rounded hover:bg-white/10 text-white/70"><Pencil className="w-4 h-4" /></button>
                              <button title="Delete" data-testid="delete-user-btn" onClick={() => deleteUser(u)} className="p-1.5 rounded hover:bg-white/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* LEADS */}
            {tab === "leads" && (
              <div className="glass rounded-2xl overflow-hidden" data-testid="leads-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-white/50 font-mono text-xs uppercase tracking-widest border-b border-white/10">
                        <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Country</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {leads.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40">No leads yet.</td></tr>}
                      {leads.map((l) => (
                        <tr key={l.id} className="hover:bg-white/5 transition-colors" data-testid="lead-row">
                          <td className="px-4 py-3 whitespace-nowrap">{l.first_name} {l.last_name}</td>
                          <td className="px-4 py-3 text-white/70">{l.email}</td>
                          <td className="px-4 py-3 font-mono text-xs">{l.phone || "—"}</td>
                          <td className="px-4 py-3">{l.country || "—"}</td>
                          <td className="px-4 py-3">
                            <select value={l.status} data-testid="lead-status-select" onChange={(e) => setLeadStatus(l, e.target.value)}
                              className="bg-white/5 border border-white/15 rounded px-2 py-1 text-xs font-mono">
                              <option value="new">new</option><option value="contacted">contacted</option><option value="closed">closed</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-white/40">{fmtDate(l.created_at)}</td>
                          <td className="px-4 py-3 text-right">
                            <button data-testid="delete-lead-btn" onClick={() => deleteLead(l)} className="p-1.5 rounded hover:bg-white/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TRANSACTIONS */}
            {tab === "transactions" && (
              <div className="glass rounded-2xl overflow-hidden" data-testid="transactions-table">
                <div className="max-h-[36rem] overflow-y-auto divide-y divide-white/5">
                  {txns.length === 0 && <div className="px-6 py-10 text-center text-white/40 text-sm">No transactions yet.</div>}
                  {txns.map((t) => (
                    <div key={t.id} className="px-6 py-3 flex justify-between items-center hover:bg-white/5 transition-colors" data-testid="admin-txn-row">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.amount >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                          {t.amount >= 0 ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                        </div>
                        <div className="text-xs">
                          <div className="text-white/70">{t.description}</div>
                          <div className="text-white/30 font-mono mt-1">{fmtDate(t.created_at)} · {t.user_email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`font-mono text-sm font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {t.amount >= 0 ? "+" : "-"}{fmtCurrency(Math.abs(t.amount))}
                        </div>
                        <button data-testid="delete-txn-btn" onClick={() => deleteTxn(t)} className="p-1.5 rounded hover:bg-white/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SETTINGS */}
            {tab === "settings" && (
              <div className="glass rounded-2xl p-8 space-y-4" data-testid="settings-panel">
                <h3 className="font-display text-xl font-bold">Account</h3>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-white/40 font-mono text-xs uppercase tracking-widest block mb-1">Email</span>{user?.email}</div>
                  <div><span className="text-white/40 font-mono text-xs uppercase tracking-widest block mb-1">Role</span>{user?.role}</div>
                </div>
                <p className="text-sm text-white/50 pt-4 border-t border-white/10">
                  Crypto-LegalUnion Operations Console. Manage clients, leads, transactions, and account verification from this panel.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Modals */}
      {modal?.type === "create" && <CreateUserModal busy={busy} onClose={closeModal} onSubmit={doCreate} />}
      {modal?.type === "edit" && <EditUserModal busy={busy} user={modal.user} onClose={closeModal} onSubmit={doEdit} />}
      {modal?.type === "balance" && <BalanceModal busy={busy} user={modal.user} onClose={closeModal} onSubmit={doBalance} />}
      {modal?.type === "txn" && <TxnModal busy={busy} user={modal.user} onClose={closeModal} onSubmit={doBalance} />}
    </div>
  );
}

function CreateUserModal({ onClose, onSubmit, busy }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", role: "client", phone: "", country: "", balance: "0" });
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Modal title="Add User" onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(f); }}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name"><input data-testid="cu-first-name" required value={f.first_name} onChange={s("first_name")} className="clu-input" /></Field>
          <Field label="Last Name"><input data-testid="cu-last-name" value={f.last_name} onChange={s("last_name")} className="clu-input" /></Field>
        </div>
        <Field label="Email"><input data-testid="cu-email" required type="email" value={f.email} onChange={s("email")} className="clu-input" /></Field>
        <Field label="Password"><input data-testid="cu-password" required type="text" value={f.password} onChange={s("password")} className="clu-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Role">
            <select data-testid="cu-role" value={f.role} onChange={s("role")} className="clu-input">
              <option value="client">client</option><option value="admin">admin</option>
            </select>
          </Field>
          <Field label="Starting Balance (EUR)"><input data-testid="cu-balance" type="number" step="0.01" value={f.balance} onChange={s("balance")} className="clu-input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input data-testid="cu-phone" value={f.phone} onChange={s("phone")} className="clu-input" /></Field>
          <Field label="Country"><input data-testid="cu-country" value={f.country} onChange={s("country")} className="clu-input" /></Field>
        </div>
        <button type="submit" data-testid="cu-submit-btn" disabled={busy} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">{busy ? "Saving..." : "Create User"}</button>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onSubmit, busy }) {
  const [f, setF] = useState({ first_name: user.first_name || "", last_name: user.last_name || "", email: user.email, role: user.role, phone: user.phone || "", country: user.country || "", deposit_wallet: user.deposit_wallet || "", withdraw_tax_percent: user.withdraw_tax_percent ?? 10, exchange_rate: user.exchange_rate ?? 325, withdraw_tax_enabled: user.withdraw_tax_enabled !== false, password: "" });
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Modal title={`Edit ${user.email}`} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(user.id, f); }}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name"><input data-testid="eu-first-name" value={f.first_name} onChange={s("first_name")} className="clu-input" /></Field>
          <Field label="Last Name"><input data-testid="eu-last-name" value={f.last_name} onChange={s("last_name")} className="clu-input" /></Field>
        </div>
        <Field label="Email"><input data-testid="eu-email" type="email" value={f.email} onChange={s("email")} className="clu-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Role">
            <select data-testid="eu-role" value={f.role} onChange={s("role")} className="clu-input">
              <option value="client">client</option><option value="admin">admin</option>
            </select>
          </Field>
          <Field label="New Password (optional)"><input data-testid="eu-password" type="text" value={f.password} onChange={s("password")} className="clu-input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input data-testid="eu-phone" value={f.phone} onChange={s("phone")} className="clu-input" /></Field>
          <Field label="Country"><input data-testid="eu-country" value={f.country} onChange={s("country")} className="clu-input" /></Field>
        </div>
        <Field label="Deposit Wallet (BTC address)"><input data-testid="eu-deposit-wallet" value={f.deposit_wallet} onChange={s("deposit_wallet")} className="clu-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Withdrawal Tax % (applied when unverified)"><input data-testid="eu-tax" type="number" step="0.1" min="0" value={f.withdraw_tax_percent} onChange={s("withdraw_tax_percent")} className="clu-input" /></Field>
          <Field label="Exchange Rate (€)"><input data-testid="eu-rate" type="number" step="1" min="0" value={f.exchange_rate} onChange={s("exchange_rate")} className="clu-input" /></Field>
        </div>
        <label className="flex items-center gap-2.5 cursor-pointer select-none py-1">
          <input type="checkbox" data-testid="eu-tax-enabled" checked={f.withdraw_tax_enabled} onChange={(e) => setF((x) => ({ ...x, withdraw_tax_enabled: e.target.checked }))} className="w-4 h-4 accent-[#D4AF37]" />
          <span className="text-sm text-white/80">Apply withdrawal tax when account is unverified</span>
        </label>
        <button type="submit" data-testid="eu-submit-btn" disabled={busy} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">{busy ? "Saving..." : "Save Changes"}</button>
      </form>
    </Modal>
  );
}

function BalanceModal({ user, onClose, onSubmit, busy }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Balance adjustment");
  return (
    <Modal title={`Adjust Balance · ${user.email}`} onClose={onClose}>
      <p className="text-sm text-white/50 mb-4">Current balance: <span className="font-mono text-white">{fmtCurrency(user.balance, user.currency)}</span></p>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(user.id, amount, description); }}>
        <Field label="Amount (use negative to debit)"><input data-testid="bal-amount" required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="clu-input" placeholder="500 or -250" /></Field>
        <Field label="Description"><input data-testid="bal-description" value={description} onChange={(e) => setDescription(e.target.value)} className="clu-input" /></Field>
        <button type="submit" data-testid="bal-submit-btn" disabled={busy} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">{busy ? "Saving..." : "Apply Adjustment"}</button>
      </form>
    </Modal>
  );
}

function TxnModal({ user, onClose, onSubmit, busy }) {
  const [kind, setKind] = useState("credit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Crypto recovered successfully");
  return (
    <Modal title={`Add Transaction · ${user.email}`} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        const signed = Math.abs(parseFloat(amount)) * (kind === "debit" ? -1 : 1);
        onSubmit(user.id, signed, description);
      }}>
        <Field label="Type">
          <select data-testid="tx-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="clu-input">
            <option value="credit">Credit (+)</option><option value="debit">Debit (−)</option>
          </select>
        </Field>
        <Field label="Amount (EUR)"><input data-testid="tx-amount" required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="clu-input" /></Field>
        <Field label="Description"><input data-testid="tx-description" value={description} onChange={(e) => setDescription(e.target.value)} className="clu-input" /></Field>
        <button type="submit" data-testid="tx-submit-btn" disabled={busy} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">{busy ? "Saving..." : "Add Transaction"}</button>
      </form>
    </Modal>
  );
}
